import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { money } from '../../../lib/money';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ConfirmDialog, DataTable, Drawer, EmptyState, Spinner, useToast } from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { itemPath } from '../../../lib/slug';
import { catalogApi } from '../../catalog/catalogApi';
import { useCatalogItemPicker } from '../../catalog/useCatalogItemPicker';
import SupplierCatalogItemForm from './SupplierCatalogItemForm';

export default function SupplierCatalogTab({ supplierId, defaultCurrency = 'USD', canManage, onChanged }) {
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();

  const [links, setLinks] = useState([]);
  // No status filter: a price list may reference items that are not active yet.
  const itemPicker = useCatalogItemPicker({ status: null });
  const items = itemPicker.items;
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const itemName = (id) => items.find((i) => i.id === id)?.name || id;
  const itemSku = (id) => items.find((i) => i.id === id)?.sku;
  const unitCode = (id) => units.find((u) => u.id === id)?.code;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, us, cats] = await Promise.all([
        catalogApi.listSupplierCatalog(supplierId),
        catalogApi.listUnits().catch(() => []),
        catalogApi.listCategories().catch(() => []),
      ]);
      setLinks(catalog || []);
      setUnits(us || []);
      setCategories(cats || []);
    } catch (err) {
      toast.error(t('Could not load catalog'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const linkedItemIds = useMemo(() => links.map((l) => l.itemId), [links]);

  // Names for already-linked items, which may sit outside the picker's first catalog page.
  useEffect(() => {
    itemPicker.ensureIds(linkedItemIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedItemIds]);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (l) => { setEditing(l); setDrawerOpen(true); };

  const save = async ({ kind, payload }) => {
    setSubmitting(true);
    try {
      if (kind === 'update') {
        await catalogApi.updateItemSupplier(editing.itemId, editing.id, payload);
        toast.success(t('Price list updated'));
      } else {
        await catalogApi.addSupplierCatalogItem(supplierId, payload);
        toast.success(payload.newItem ? t('Item created and added') : t('Item added to price list'));
      }
      setDrawerOpen(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not save'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeItemSupplier(deleteTarget.itemId, deleteTarget.id);
      toast.success(t('Item removed from price list'));
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not remove'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'item',
      header: t('Item'),
      width: 260,
      render: (row) => (
        <div className="pp-link-cell" onClick={(e) => { e.stopPropagation(); navigate(itemPath({ id: row.itemId, name: itemName(row.itemId) })); }}>
          <span style={{ fontWeight: 600 }}>{itemName(row.itemId)}</span>
          {itemSku(row.itemId) && <span className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{itemSku(row.itemId)}</span>}
        </div>
      ),
    },
    {
      key: 'flags', header: '', width: 130, render: (row) => (
        <div style={{ display: 'inline-flex', gap: 'var(--space-1-5)' }}>
          {row.isPreferred && <Badge tone="brand">{t('Preferred')}</Badge>}
          {!row.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
        </div>
      ),
    },
    { key: 'sku', header: t('Supplier SKU'), width: 140, render: (row) => row.supplierSku || <span className="pp-cell-muted">—</span> },
    { key: 'price', header: t('Price'), width: 130, align: 'right', render: (row) => (row.price ? money(row.price) : <span className="pp-cell-muted">—</span>) },
    { key: 'pack', header: t('Pack'), width: 110, render: (row) => `${row.packSize}${unitCode(row.purchaseUomId) ? ` ${unitCode(row.purchaseUomId)}` : ''}` },
    { key: 'moq', header: t('MOQ'), width: 80, align: 'right', render: (row) => (row.minimumOrderQuantity ?? <span className="pp-cell-muted">—</span>) },
    { key: 'lead', header: t('Lead'), width: 80, align: 'right', render: (row) => (row.leadTimeDays != null ? t('{days}d', { days: row.leadTimeDays }) : <span className="pp-cell-muted">—</span>) },
    {
      key: 'actions', header: '', width: 90, align: 'right', resizable: false,
      render: (row) => canManage ? (
        <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton aria-label={t('Edit')} variant="ghost" size="icon-sm" onClick={() => openEdit(row)}><Icon.Pencil size={15} /></IconButton>
          <IconButton aria-label={t('Remove')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(row)}><Icon.Trash size={15} /></IconButton>
        </div>
      ) : null,
    },
  ];

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Add item')}</Button>
        )}
      </div>

      {links.length === 0 ? (
        <EmptyState
          icon={<Icon.Package size={28} />}
          title={t('No items on this price list')}
          description={t('Add catalog items this supplier sells, with their prices and pack sizes. You can link existing items or create new ones.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('Add item')}</Button> : null}
        />
      ) : (
        <DataTable columns={columns} data={links} rowKey={(r) => r.id} density="comfortable" />
      )}

      <Drawer open={drawerOpen} onClose={() => (submitting ? null : setDrawerOpen(false))} title={editing ? t('Edit price list entry') : t('Add item to price list')} size="md">
        <SupplierCatalogItemForm
          link={editing}
          items={items}
          onItemSearch={itemPicker.onSearchChange}
          itemsSearching={itemPicker.searching}
          units={units}
          categories={categories}
          defaultCurrency={defaultCurrency}
          linkedItemIds={linkedItemIds}
          submitting={submitting}
          onSubmit={save}
          onCancel={() => setDrawerOpen(false)}
        />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Remove item')}
        description={deleteTarget ? t('Remove "{name}" from this supplier\'s price list? The catalog item itself is not deleted.', { name: itemName(deleteTarget.itemId) }) : ''}
        confirmLabel={t('Remove')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
